import type { FormBlock as FormBlockDoc, FormField } from '../types';

export type FieldValue = string | boolean;

/**
 * Formulář vrstvy. E-mail sbírá VÝHRADNĚ přes stejnou cestu jako newsletter
 * (řeší LayerRenderer). Tady jen vykreslení polí a odeslací tlačítko —
 * validaci a odeslání drží renderer.
 */
export function FormBlock({
  block,
  values,
  setValue,
  status,
  errorMessage,
}: {
  block: FormBlockDoc;
  values: Readonly<Record<string, FieldValue>>;
  setValue: (name: string, value: FieldValue) => void;
  status: 'idle' | 'sending' | 'error';
  errorMessage: string;
}) {
  return (
    <div className="layer-form">
      {block.fields.map((field) => (
        <Field key={field.name} field={field} value={values[field.name]} setValue={setValue} />
      ))}

      {status === 'error' && errorMessage !== '' && (
        <p className="layer-form-error" role="alert">
          {errorMessage}
        </p>
      )}

      <button type="submit" className="layer-button layer-button-primary" disabled={status === 'sending'}>
        {status === 'sending' ? 'Odesílám…' : block.submitLabel}
      </button>
    </div>
  );
}

function Field({
  field,
  value,
  setValue,
}: {
  field: FormField;
  value: FieldValue | undefined;
  setValue: (name: string, value: FieldValue) => void;
}) {
  const id = `layer-field-${field.name}`;

  if (field.type === 'checkbox') {
    return (
      <label className="layer-field layer-field-checkbox" htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          checked={value === true}
          required={field.required}
          onChange={(event) => setValue(field.name, event.target.checked)}
        />
        <span>
          {field.label}
          {field.required && <span aria-hidden="true"> *</span>}
        </span>
      </label>
    );
  }

  if (field.type === 'select') {
    return (
      <label className="layer-field" htmlFor={id}>
        <span className="layer-field-label">
          {field.label}
          {field.required && <span aria-hidden="true"> *</span>}
        </span>
        <select
          id={id}
          className="layer-field-input"
          value={typeof value === 'string' ? value : ''}
          required={field.required}
          onChange={(event) => setValue(field.name, event.target.value)}
        >
          <option value="">—</option>
          {(field.options ?? []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    );
  }

  if (field.type === 'radio') {
    return (
      <fieldset className="layer-field layer-field-radio">
        <legend className="layer-field-label">
          {field.label}
          {field.required && <span aria-hidden="true"> *</span>}
        </legend>
        {(field.options ?? []).map((option) => (
          <label key={option.value} className="layer-radio-option">
            <input
              type="radio"
              name={field.name}
              value={option.value}
              checked={value === option.value}
              required={field.required}
              onChange={(event) => setValue(field.name, event.target.value)}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </fieldset>
    );
  }

  // email / phone
  return (
    <label className="layer-field" htmlFor={id}>
      <span className="layer-field-label">
        {field.label}
        {field.required && <span aria-hidden="true"> *</span>}
      </span>
      <input
        id={id}
        className="layer-field-input"
        type={field.type === 'email' ? 'email' : 'tel'}
        inputMode={field.type === 'phone' ? 'tel' : 'email'}
        autoComplete={field.type === 'email' ? 'email' : 'tel'}
        value={typeof value === 'string' ? value : ''}
        required={field.required}
        {...(field.pattern === undefined ? {} : { pattern: field.pattern })}
        onChange={(event) => setValue(field.name, event.target.value)}
      />
    </label>
  );
}
